import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { colors, typography } from '@pray/shared/components/foundations';
import Metadata from '@pray/shared/components/Metadata';
import Text from '@pray/shared/components/ui/Text';
import Spinner from '@pray/shared/components/v1/Spinner/Spinner';
import logger from '@pray/shared/utils/logger';

import { useStudioContext } from 'context/StudioContext';
import useYouTubeOAuth from 'hooks/socials/useYouTubeOAuth';

type CallbackStatus = 'processing' | 'success' | 'error';

export default function YouTubeOAuthCallback() {
  const [searchParams] = useSearchParams();
  const { selectedArtist } = useStudioContext();
  const { handleOAuthCallback } = useYouTubeOAuth({ artistId: selectedArtist?.id });

  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const contextTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fail fast if artist context hasn't loaded within 10 seconds (popup may lack session)
  useEffect(() => {
    contextTimeoutRef.current = setTimeout(() => {
      if (!selectedArtist?.id) {
        setStatus('error');
        setErrorMessage('Session unavailable. Please close this window and try again.');
        setTimeout(window.close, 5000);
      }
    }, 10000);

    return () => {
      if (contextTimeoutRef.current) clearTimeout(contextTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!selectedArtist?.id) return;

    if (contextTimeoutRef.current) {
      clearTimeout(contextTimeoutRef.current);
      contextTimeoutRef.current = null;
    }

    const processCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code || !state) {
          throw new Error('Missing required OAuth parameters');
        }

        const success = await handleOAuthCallback(code, state);
        if (success) {
          setStatus('success');
          setTimeout(window.close, 2000);
        } else {
          throw new Error('Failed to complete OAuth flow');
        }
      } catch (err) {
        logger.error('YouTube OAuth callback error', err);
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'An unknown error occurred');
        setTimeout(window.close, 5000);
      }
    };

    processCallback();
  }, [selectedArtist?.id]);

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="flex flex-col items-center gap-4">
            <Spinner size="large" />
            <Text variant={typography.heading_md} color={colors.text_secondary}>
              Connecting your YouTube account...
            </Text>
            <Text variant={typography.body_md} color={colors.text_tertiary}>
              Please wait while we complete the connection.
            </Text>
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="flex size-16 items-center justify-center rounded-full bg-green-100">
              <svg className="size-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <Text variant={typography.heading_md} color={colors.text_secondary}>
              YouTube Connected Successfully!
            </Text>
            <Text variant={typography.body_md} color={colors.text_tertiary}>
              This window will close automatically in a moment.
            </Text>
          </div>
        );

      case 'error':
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="flex size-16 items-center justify-center rounded-full bg-red-100">
              <svg className="size-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <Text variant={typography.heading_md} color={colors.text_secondary}>
              Connection Failed
            </Text>
            <Text variant={typography.body_md} color={colors.text_tertiary}>
              {errorMessage || 'Unable to connect your YouTube account. Please try again.'} This window will close
              automatically.
            </Text>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen w-full items-center justify-center bg-gray-50">
      <Metadata title="YouTube OAuth - Pray Studio" />
      <div className="w-full max-w-md p-8">{renderContent()}</div>
    </div>
  );
}
